import type { SVGProps } from 'react';
import { cn } from '../lib/cn';

/**
 * Editorial line icons for the sidebar nav.
 * All icons share viewBox 0 0 24 24, stroke=currentColor, strokeWidth=1.5,
 * so they inherit text colour (ink-2 inactive → accent active) and align
 * with the Cocoa & Paper visual weight.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({
  size = 18,
  className,
  children,
  ...rest
}: IconProps & { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Today — calendar with today-dot */
export function TodayIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
      <circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Catalogue — book / spread */
export function CatalogueIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 5.5C3 4.67 3.67 4 4.5 4H11v15H4.5C3.67 19 3 18.33 3 17.5v-12z" />
      <path d="M21 5.5c0-.83-.67-1.5-1.5-1.5H13v15h6.5c.83 0 1.5-.67 1.5-1.5v-12z" />
      <path d="M11 19v2M13 19v2" opacity="0.5" />
    </Svg>
  );
}

/** Orders — receipt with serrated edge */
export function OrdersIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M6 3h12v18l-2-1.5L13.5 21 12 19.5 10.5 21 8 19.5 6 21V3z" />
      <path d="M9 8h6M9 12h6M9 15.5h4" />
    </Svg>
  );
}

/** Quotations — document with corner fold (a proposal) */
export function QuotationsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M5 3.5h10l4 4v13a0.5 0.5 0 01-.5 0.5h-13a0.5 0.5 0 01-.5-0.5V4a0.5 0.5 0 01.5-0.5z" />
      <path d="M15 3.5v3.5a0.5 0.5 0 00.5 0.5H19" />
      <path d="M8 13h6M8 16h5" />
      <path d="M8 9.5h3" opacity="0.55" />
    </Svg>
  );
}

/** Customers — single person */
export function CustomersIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20.5c0-3.87 3.13-7 7-7s7 3.13 7 7" />
    </Svg>
  );
}

/** Stock — stacked boxes / pallet */
export function StockIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 7.5l9-4.5 9 4.5-9 4.5-9-4.5z" />
      <path d="M3 7.5v9l9 4.5 9-4.5v-9" />
      <path d="M12 12v9" opacity="0.55" />
    </Svg>
  );
}

/** Reservations — bookmark / tag (stock held against an order) */
export function ReserveIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M6 3.5h12a1 1 0 011 1V21l-7-4-7 4V4.5a1 1 0 011-1z" />
      <path d="M9 9h6" opacity="0.55" />
    </Svg>
  );
}

/** Cash close — banknote */
export function CashIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="2.5" y="6.5" width="19" height="11" rx="1.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M5.5 9.25v.01M18.5 14.75v.01" />
    </Svg>
  );
}

/** Finance — line chart with hero point */
export function FinanceIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3.5 20.5h17" />
      <path d="M5 16.5l4-5 4 3 5.5-7.5" />
      <circle cx="18.5" cy="7" r="1.5" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Operations — clipboard with check */
export function OperationsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="5.5" y="4.5" width="13" height="16" rx="1.5" />
      <path d="M9 4.5V3.5a1.5 1.5 0 011.5-1.5h3A1.5 1.5 0 0115 3.5v1" />
      <path d="M9 13l2 2 4-4" />
    </Svg>
  );
}

/** Manufacturing — factory roofline */
export function ManufactureIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 21V10l5 3.5V10l5 3.5V10l5 3.5V21z" />
      <path d="M3 21h18" />
      <path d="M7 17h.01M11 17h.01M15 17h.01" />
    </Svg>
  );
}

/** Suppliers / purchase orders — inbound tray with down-arrow */
export function SupplierIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M4 13v6a1 1 0 001 1h14a1 1 0 001-1v-6" />
      <path d="M4 13h4l1.5 2.5h5L16 13h4" />
      <path d="M12 3v6" />
      <path d="M9 6.5l3 3 3-3" />
    </Svg>
  );
}

/** Shops — storefront with awning */
export function ShopsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M4 9h16l-1-5H5L4 9z" />
      <path d="M5 9v11.5h14V9" />
      <path d="M10 20.5V14h4v6.5" />
    </Svg>
  );
}

/** Users — two people */
export function UsersIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.31 2.69-6 6-6s6 2.69 6 6" />
      <circle cx="17" cy="7.5" r="2.25" />
      <path d="M15.5 13.6c.5-.2 1.05-.3 1.6-.3 2.49 0 4.5 2.01 4.5 4.5" />
    </Svg>
  );
}

/** Categories — labelled folder */
export function CategoriesIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 7a2 2 0 012-2h3.5l2 2H19a2 2 0 012 2v8.5a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
      <path d="M3 10.5h18" opacity="0.4" />
    </Svg>
  );
}

/** Products — cube */
export function ProductsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M12 3l8.5 4.5v9L12 21l-8.5-4.5v-9L12 3z" />
      <path d="M3.5 7.5L12 12l8.5-4.5M12 12v9" />
    </Svg>
  );
}

/** Credits — return arrow inside an invoice */
export function CreditsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M9 13l-2 2 2 2" />
      <path d="M7 15h6a3 3 0 003-3v-1" />
    </Svg>
  );
}

/** Delivery — truck */
export function DeliveryNoteIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 6h11v9H3z" />
      <path d="M14 9h4l3 3v3h-7" />
      <circle cx="7" cy="17" r="1.6" />
      <circle cx="17" cy="17" r="1.6" />
    </Svg>
  );
}

/** Remittance — money envelope */
export function RemittanceIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="3" y="6" width="18" height="13" rx="1.5" />
      <path d="M3 7l9 7 9-7" />
      <path d="M12 11v4" />
      <path d="M10 13h4" />
    </Svg>
  );
}

/** Bank deposit — bank columns */
export function BankIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M3 9l9-5 9 5" />
      <path d="M5 9v8M9 9v8M15 9v8M19 9v8" />
      <path d="M3 19h18" />
    </Svg>
  );
}

/** Petty cash — wallet */
export function PettyCashIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M16 12.5h2.5" />
      <path d="M3 9h12V6" />
    </Svg>
  );
}

/** Journal voucher — ledger book */
export function JournalIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M5 4h12a2 2 0 012 2v14H7a2 2 0 01-2-2z" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </Svg>
  );
}

/** Discount approvals — percent tag with a check */
export function DiscountIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <path d="M19 5 5 19" />
      <circle cx="7.5" cy="7.5" r="2.2" />
      <circle cx="16.5" cy="16.5" r="2.2" />
    </Svg>
  );
}

/** Settings — gear */
export function SettingsIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </Svg>
  );
}

/** Audit log — clipboard with ticked lines */
export function AuditIcon(p: IconProps): JSX.Element {
  return (
    <Svg {...p}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4a1.5 1.5 0 013 0h0a1.5 1.5 0 01-3 0z" transform="translate(1.5 0)" />
      <path d="M8.5 10.5l1.2 1.2 2-2" />
      <path d="M13.5 10.5H16" />
      <path d="M8.5 15.5l1.2 1.2 2-2" />
      <path d="M13.5 15.5H16" />
    </Svg>
  );
}
